
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SpinnerService } from '@core/services/spinner.service';
import { FundingSourceTypes } from '@core/typings/budget.typing';
import { Collaborator } from '@core/typings/collaboration.typing';
import { OrganizationForInfoPanel } from '@core/typings/organization.typing';
import { Payment, ProcessingTypes } from '@core/typings/payment.typing';
import { ApplicationStatuses, PaymentStatus } from '@core/typings/status.typing';
import { AwardService } from '@features/awards/award.service';
import { Award, AwardFromApi, AwardModalResponse } from '@features/awards/typings/award.typing';
import { BudgetService } from '@features/budgets/budget.service';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { SpecialHandling } from '@features/forms/form-renderer-components/standard-form-components/form-special-handling/form-special-handling.component';
import { InKindRequestedItem } from '@features/in-kind/in-kind.typing';
import { ProgramService } from '@features/programs/services/program.service';
import { OrganizationEligibleForGivingStatus, SimpleStringMap } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmationModalComponent, ModalFactory } from '@yourcause/common/modals';
import { SimpleAwardModalComponent } from '../simple-award-modal/simple-award-modal.component';

@Component({
    selector: 'gc-awards',
    templateUrl: 'awards.component.html',
    styleUrls: ['./awards.component.scss'],
    standalone: false
})
export class AwardsComponent implements OnInit, OnChanges {
  // Required
  @Input() applicationId: number;
  @Input() awards: Award[] = [];
  @Input() awardsFromApi: AwardFromApi[] = [];
  // The applicant portal version is a simple view with only relevant info and no actions
  @Input() isForApplicantPortal: boolean;

  // Only for GM portal
  @Input() notifyApplicant: boolean;
  @Input() applicantEmail: string;
  @Input() amountRequested: number;
  @Input() recommendedFundingAmount: number;
  @Input() requestedItems: InKindRequestedItem[];
  @Input() currencyRequestedAmountEquivalent: number;
  @Input() designation: string;
  @Input() programId: number;
  @Input() cycleId: number;
  @Input() canAward = true;
  @Input() isArchived = false;
  @Input() currencyRequested: string;
  @Input() canTakeActions = true;
  @Input() assignedBudgetId: number;
  @Input() assignedFsId: number;
  @Input() reservedFunds = false;
  @Input() specialHandling: SpecialHandling;
  @Input() isInternational = false;
  @Input() applicantApplying = false;
  @Input() organizationEligibleForGivingStatus: OrganizationEligibleForGivingStatus;
  @Input() registrationAuthorityName: string;
  @Input() cycleBudgets: number[];
  @Input() isMasked = false;
  @Input() applicant: Collaborator;
  @Input() organization: OrganizationForInfoPanel;
  @Input() applicationStatus: ApplicationStatuses;
  @Output() onAwardChange = new EventEmitter();

  OrganizationEligibleForGivingStatus = OrganizationEligibleForGivingStatus;
  ProcessingType = ProcessingTypes;
  FundingSourceType = FundingSourceTypes;
  PaymentStatus = PaymentStatus;
  originalAwards: Award[];
  paidString = this.i18n.translate('GLOBAL:textPaid');
  pendingString = this.i18n.translate('common:lblPending');
  showInDefault = false;
  showInDefaultCurrencyText = this.i18n.translate(
    'GLOBAL:textShowInDefaultCurrency',
    {},
    'Show in default currency'
  );
  showInRequestedCurrencyText = this.i18n.translate(
    'GLOBAL:textShowInRequestedCurrency',
    {},
    'Show in requested currency'
  );
  globeTooltip = this.showInDefaultCurrencyText;
  defaultCurrency = this.clientSettingsService.defaultCurrency;
  showAmountToggle = false;
  canAddPaymentMap: SimpleStringMap<boolean> = {};
  ProcessingTypes = ProcessingTypes;
  hideFundingSources = this.clientSettingsService.hideFundingSources;

  constructor (
    private i18n: I18nService,
    private modalFactory: ModalFactory,
    private spinnerService: SpinnerService,
    private awardService: AwardService,
    private programService: ProgramService,
    private budgetService: BudgetService,
    private clientSettingsService: ClientSettingsService
  ) { }

  ngOnInit () {
    this.originalAwards = this.awards.map((award) => {
      return {
        ...award,
        payments: award.payments.map((p) => ({...p}))
      };
    });
    if (!this.currencyRequested) {
      this.currencyRequested = this.defaultCurrency;
    }
    if (
      !this.isForApplicantPortal &&
      this.currencyRequested !== this.defaultCurrency
    ) {
      this.showAmountToggle = true;
    }
  }

  ngOnChanges (changes: SimpleChanges) {
    if (changes.awards) {
      this.awards.forEach((award) => {
        const paymentTotal = award.payments.reduce((acc, pay) => {
          if (pay.statusId !== PaymentStatus.Voided) {
            return +acc + +pay.amount;
          }
          
          return acc;
        }, 0);
        const awardTotal = award.amount;
        if (!this.isForApplicantPortal) {
          const hasAvailableSources = this.awardService.checkVettingRequirementForAward(
            this.organizationEligibleForGivingStatus,
            this.registrationAuthorityName,
            this.cycleBudgets,
            null,
            null,
            null,
            true
          );
          this.canAddPaymentMap[award.id] = (awardTotal > paymentTotal) &&
            hasAvailableSources;
        }
      });
    }
  }

  toggleAmounts () {
    this.showInDefault = !this.showInDefault;
    if (this.showInDefault) {
      this.globeTooltip = this.showInRequestedCurrencyText;
    } else {
      this.globeTooltip = this.showInDefaultCurrencyText;
    }
  }

  async deleteAward (row: Award) {
    const deps = {
      confirmButtonText: this.i18n.translate(
        'common:btnDelete',
        {},
        'Delete'
      ),
      confirmText: this.i18n.translate(
        'AWARDS:textAreYouSureDeleteAward',
        {},
        'Are you sure you want to delete this award? This action cannot be undone.'
      ),
      modalHeader: this.i18n.translate(
        'AWARDS:hdrDeleteAward',
        {},
        'Delete Award'
      )
    };
    const proceed = await this.modalFactory.open(ConfirmationModalComponent, deps);
    if (proceed) {
      await this.awardService.handleDeleteAward(row, this.applicationId);
      this.onAwardChange.emit();
    }
  }

  async awardModal (
    award?: Award,
    payment?: Payment,
    isAddPayment = false
  ) {
    const response = await this.modalFactory.open(
      SimpleAwardModalComponent,
      {
        awards: this.awardsFromApi,
        awardOnly: !payment && !isAddPayment,
        addPaymentOnly: isAddPayment,
        originalAward: award,
        originalPayment: payment,
        requestedItems: this.requestedItems,
        applicantCanReceiveEmails: this.notifyApplicant,
        amountRequested: this.amountRequested,
        programId: this.programId,
        applicationId: this.applicationId,
        appDesignation: this.designation,
        currencyRequested: this.currencyRequested,
        currencyRequestedAmountEquivalent: this.currencyRequestedAmountEquivalent,
        existingAwardTypes: this.awards.map((a) => a.awardType),
        cycleId: this.cycleId,
        assignedBudgetId: this.assignedBudgetId,
        assignedFsId: this.assignedFsId,
        reservedFunds: this.reservedFunds,
        specialHandling: this.specialHandling,
        organizationEligibleForGivingStatus: this.organizationEligibleForGivingStatus,
        recommendedFundingAmount: this.recommendedFundingAmount,
        isMasked: this.isMasked,
        applicant: this.applicant,
        organization: this.organization,
        orgIsInternational: this.isInternational
      }
    );
    if (response) {
      await this.handleAwardUpdate(response);
    }
  }

  async handleAwardUpdate (response: AwardModalResponse) {
    this.spinnerService.startSpinner();
    const awardId = await this.awardService.handleAwardModalUpdates(
      response,
      this.applicationId
    );
    await this.resetBudgetDetail();
    this.spinnerService.stopSpinner();
    if (response.addAnotherPayment) {
      const awards = await this.awardService.getAwards(this.applicationId);
      const adapted = this.awardService.constructAwards(awards.awards);
      const found = adapted.find((award) => award.id === awardId);
      if (found) {
        // trying this to fix GC-7840. can't recreate locally so seeing if this helps. If it does not I will remove
        setTimeout(() => {
          this.awardModal(found, null, true);
        });
      }
    }
    this.onAwardChange.emit();
  }

  async resetBudgetDetail () {
    const cycle = await this.programService.getCycleFromProgram(
      this.programId,
      this.cycleId
    );
    this.budgetService.clearBudgetDetails(cycle.budgetIds);
  }

  async deletePayment (awardId: number, paymentId: number) {
    const deps = {
      modalHeader: this.i18n.translate(
        'AWARDS:hdrDeletePayment'
      ),
      confirmButtonText: this.i18n.translate(
        'common:btnDelete',
        {},
        'Delete'
      ),
      confirmText: this.i18n.translate(
        'AWARDS:textAreYouSureDeletePayment',
        {},
        'Are you sure you want to delete this payment? This action cannot be undone.'
      )
    };
    const proceed = await this.modalFactory.open(ConfirmationModalComponent, deps);
    if (proceed) {
      await this.awardService.removePayment(
        this.applicationId,
        awardId,
        paymentId
      );
      this.onAwardChange.emit();
    }
  }
}
